import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Clock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  fetchPublicSignRequest,
  PhoneSignError,
  submitPublicSignature,
  type PublicSignRequest,
} from '@/api/portage'
import { SignaturePad, type SignaturePadHandle } from '@/components/manager/numbers/portage/SignaturePad'
import { formatNanp } from '@/components/manager/numbers/portage/portageUtils'
import { useDeviceWidth } from '@/hooks/useDeviceWidth'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useUIStore } from '@/stores/useUIStore'

type View =
  | { kind: 'loading' }
  | { kind: 'ready'; request: PublicSignRequest }
  | { kind: 'done' }
  | { kind: 'expired' }
  | { kind: 'used' }
  | { kind: 'loadError' }

/** A code that no longer works: used, or anything else (expired, replaced, letter changed). */
function viewForError(err: unknown): View {
  if (err instanceof PhoneSignError && err.code.startsWith('code_')) {
    return err.code === 'code_signe' ? { kind: 'used' } : { kind: 'expired' }
  }
  return { kind: 'loadError' }
}

/**
 * Signing the authorization letter with a finger, opened from the QR code shown on the computer.
 * Public: the code alone opens it, without logging in. /{lang}/signer/{code}
 */
export function SignByPhonePage() {
  const { t } = useTranslation()
  const { code = '' } = useParams<{ code: string }>()
  const darkMode = useUIStore((s) => s.darkMode)
  const [view, setView] = useState<View>({ kind: 'loading' })
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const pad = useRef<SignaturePadHandle>(null)

  useDeviceWidth()
  useDocumentTitle(t('manager.numbers.port.phonePage.title'))

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode)
  }, [darkMode])

  const load = useCallback(async () => {
    setView({ kind: 'loading' })
    try {
      setView({ kind: 'ready', request: await fetchPublicSignRequest(code) })
    } catch (err) {
      setView(viewForError(err))
    }
  }, [code])

  useEffect(() => {
    void load()
  }, [load])

  const submit = async () => {
    setError('')
    const signature = pad.current?.export()
    if (!signature) {
      setError(t('manager.numbers.port.errors.signature'))
      return
    }
    setSending(true)
    try {
      await submitPublicSignature(code, signature.png, signature.points)
      setView({ kind: 'done' })
    } catch (err) {
      if (err instanceof PhoneSignError && err.code === 'trace_absent') setError(t('manager.numbers.port.errors.signature'))
      else if (err instanceof PhoneSignError && err.code.startsWith('code_')) setView(viewForError(err))
      else setError(t('manager.numbers.port.phonePage.sendError'))
    } finally {
      setSending(false)
    }
  }

  const header = (
    <header className="px-5 pt-5 pb-4 border-b border-slate-200 dark:border-slate-800">
      <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">Callizy</p>
      <h1 className="mt-1 text-xl font-semibold leading-snug">{t('manager.numbers.port.phonePage.title')}</h1>
    </header>
  )

  if (view.kind !== 'ready') {
    return (
      <div className="flex min-h-[100dvh] flex-col bg-background text-foreground">
        {header}
        <main className="flex flex-1 flex-col items-center justify-center gap-4 px-6 pb-16 text-center">
          {view.kind === 'loading' && <Loader2 className="h-7 w-7 animate-spin text-slate-400" />}
          {view.kind === 'done' && (
            <>
              <CheckCircle2 className="h-14 w-14 text-emerald-500" />
              <p className="text-xl font-semibold">{t('manager.numbers.port.phonePage.doneTitle')}</p>
              <p className="max-w-xs text-base text-slate-600 dark:text-slate-300">{t('manager.numbers.port.phonePage.doneText')}</p>
            </>
          )}
          {(view.kind === 'expired' || view.kind === 'used') && (
            <>
              <Clock className="h-14 w-14 text-slate-400" />
              <p className="text-xl font-semibold">
                {view.kind === 'used'
                  ? t('manager.numbers.port.phonePage.usedTitle')
                  : t('manager.numbers.port.phonePage.expiredTitle')}
              </p>
              <p className="max-w-xs text-base text-slate-600 dark:text-slate-300">{t('manager.numbers.port.phonePage.askNew')}</p>
            </>
          )}
          {view.kind === 'loadError' && (
            <>
              <p className="max-w-xs text-base text-slate-600 dark:text-slate-300">{t('manager.numbers.port.phonePage.loadError')}</p>
              <Button type="button" variant="outline" onClick={() => void load()} className="h-12 px-6 text-base">
                {t('manager.numbers.port.phonePage.retry')}
              </Button>
            </>
          )}
        </main>
      </div>
    )
  }

  const { request } = view
  const holder = `${request.first_name} ${request.last_name}`.trim()
  return (
    <div className="flex h-[100dvh] flex-col bg-background text-foreground">
      {header}
      <main className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        <dl className="divide-y divide-slate-100 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
          <div className="px-4 py-3">
            <dt className="text-xs text-slate-500 dark:text-slate-400">{t('manager.numbers.port.phonePage.holder')}</dt>
            <dd className="mt-0.5 font-medium">{holder}</dd>
            {request.business_name && (
              <dd className="text-sm text-slate-600 dark:text-slate-300">{request.business_name}</dd>
            )}
          </div>
          <div className="px-4 py-3">
            <dt className="text-xs text-slate-500 dark:text-slate-400">
              {t('manager.numbers.port.phonePage.numbers', { count: request.numbers.length })}
            </dt>
            {request.numbers.map((n) => (
              <dd key={n} className="mt-0.5 font-medium tabular-nums">
                {formatNanp(n)}
              </dd>
            ))}
          </div>
        </dl>
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{t('manager.numbers.port.phonePage.legal')}</p>
        <SignaturePad
          ref={pad}
          onInkChange={(ink) => ink && setError('')}
          padClassName="h-60"
        />
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </main>
      <footer className="border-t border-slate-200 px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-slate-800">
        <Button
          type="button"
          onClick={() => void submit()}
          disabled={sending}
          className="h-12 w-full bg-blue-500 text-base font-semibold text-white hover:bg-blue-600"
        >
          {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('manager.numbers.port.phonePage.submit')}
        </Button>
      </footer>
    </div>
  )
}
